/**
 * Every payment the workspace has raised, from `GET /api/v1/revenue/`.
 *
 * One endpoint, and it answers with two things: a page of payment requests —
 * the same record a client's history and an enquiry's are made of, down to the
 * field — and a `summary` of what has come in, what is still out and what was
 * called off. The summary is of everything the query matched rather than of the
 * page, so the two are fetched as one answer and handed back as one: ask for a
 * window and the figures are of that window, which is what keeps a tile from
 * being a sum of rows the table is not showing.
 *
 * Money crosses the wire as a decimal string — `"2000.00"` — because that is
 * how the backend keeps it. It is a number everywhere on this side.
 *
 * Reading only. A request that exists is its own record, confirmed and
 * cancelled through its own `public_id` from the screen it was raised on — a
 * client's Payments tab, or the enquiry it came from — so nothing here writes.
 */

import {
  api,
  API_ENDPOINTS,
  isPaymentRequestCancelled,
  isPaymentRequestPaid,
  paymentPlanName,
  toAmountNumber,
} from "../../../lib/api";
import type { PaymentRequestResponse } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  RevenueLedger,
  RevenueListResponse,
  RevenuePayment,
  RevenuePaymentStatus,
  RevenueSummaryResponse,
  RevenueTotals,
} from "../types/revenue.types";

/**
 * What the endpoint will narrow by. Every field is optional, and one left off
 * is not sent at all — the backend then answers with everything, which is what
 * the screen asks for when it is set to "All time".
 */
export interface RevenueFilters {
  /** Inclusive ISO dates: `from=2026-07-11&to=2026-08-11` is a month, both ends
   *  counted. The screen's period dropdown is these two — see `periodRange`. */
  from?: string;
  to?: string;
  /**
   * One backend state: `AWAITING_PAYMENT`, `CONFIRMED` or `CANCELLED`.
   *
   * The Revenue screen does not send it. Its tabs narrow a window it already
   * holds every row of, and the three tiles above them total all three states
   * whichever tab is open — asking the server per tab would cost a round trip
   * to remove rows that are already in hand, and would leave the tiles reading
   * off a summary of one state. It is here because it is the endpoint's, and
   * the next caller may well want it.
   */
  status?: "AWAITING_PAYMENT" | "CONFIRMED" | "CANCELLED";
}

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/**
 * What the row reads as, in this screen's three words. Both questions are the
 * API layer's to answer — see `isPaymentRequestPaid` — so a backend state the
 * app has never seen reads as "Pending", a question still open, rather than as
 * a blank pill.
 */
const toStatus = (response: PaymentRequestResponse): RevenuePaymentStatus => {
  if (isPaymentRequestPaid(response)) return "Paid";
  if (isPaymentRequestCancelled(response)) return "Cancelled";
  return "Pending";
};

/**
 * One row of the table, out of the record the endpoint sends.
 *
 * Every field is defended: a row the backend sent with a plan it could not
 * name is still a row the table has to draw, and a dash in a cell is a better
 * answer than a screen that fails to render.
 */
const toRevenuePayment = (
  response: PaymentRequestResponse
): RevenuePayment => ({
  id: response.public_id,
  clientName: response.name || "-",
  place: response.place || "-",
  goal: response.goal || "-",
  planName: paymentPlanName(response) || "-",
  amount: toAmountNumber(response.amount_due),
  duration: response.duration_label || "-",
  generatedAt: response.generated_on,
  // `paid_on` is the day; the receipt carries it too, and is what a row that
  // was paid without the top-level field being set still reads its date from.
  paidAt: response.paid_on ?? response.payment?.received_on ?? null,
  status: toStatus(response),
});

/** The backend's three figures, as numbers. */
const toTotals = (summary: RevenueSummaryResponse): RevenueTotals => ({
  received: toAmountNumber(summary.received),
  pending: toAmountNumber(summary.pending),
  cancelled: toAmountNumber(summary.cancelled),
});

/**
 * The same three figures worked out from the rows, for a response that carried
 * no summary. It is the definition the backend is totalling to, so a screen
 * without one shows figures rather than three zeros that would read as a
 * workspace that has taken nothing.
 */
const totalsFrom = (payments: RevenuePayment[]): RevenueTotals =>
  payments.reduce<RevenueTotals>(
    (sums, payment) => {
      if (payment.status === "Paid") sums.received += payment.amount;
      else if (payment.status === "Pending") sums.pending += payment.amount;
      else sums.cancelled += payment.amount;
      return sums;
    },
    { received: 0, pending: 0, cancelled: 0 }
  );

/**
 * How many rows a page asks for. A hundred, because the screen wants the whole
 * list at once — it filters by period, splits by status and pages in the
 * browser — so the fewer round trips the walk below takes, the better.
 */
const PAGE_SIZE = 100;

/**
 * Stops a broken `next` from looping forever. At a hundred a page this is
 * 2,000 payments, which is far past what one workspace raises.
 */
const MAX_PAGES = 20;

/** One page of the revenue list, in the order the backend sent it. */
async function fetchRevenuePage(
  getToken: GetToken,
  filters: RevenueFilters,
  page: number,
  signal?: AbortSignal
): Promise<RevenueListResponse> {
  return api.get<RevenueListResponse>(API_ENDPOINTS.revenue.list, {
    token: await token(getToken, "load your revenue"),
    // `undefined` entries are dropped by the API client, so an unset filter is
    // a parameter the request never carries rather than an empty one.
    query: {
      page,
      page_size: PAGE_SIZE,
      from: filters.from,
      to: filters.to,
      status: filters.status,
    },
    signal,
  });
}

/**
 * Every payment the query matched, newest first, with the backend's totals of
 * them beside them.
 *
 * Every page rather than one of them: the screen splits the window by status
 * and pages through it, and neither is something a single page can answer. The
 * window itself is the server's — `from` and `to` go up with the request — so
 * what comes back is what the screen is looking at, figures included.
 *
 * The totals are taken from the first page — the summary is of everything the
 * query matched rather than of the page, so a later one says the same thing —
 * and worked out from the rows only if the response carried none.
 *
 * Sorted here rather than trusted to arrive that way: the table reads top-down
 * as most-recent-first, and that is the app's decision to make, not one to
 * inherit from whatever order the pages happened to come back in.
 */
export async function fetchRevenue(
  getToken: GetToken,
  filters: RevenueFilters = {},
  signal?: AbortSignal
): Promise<RevenueLedger> {
  const payments: RevenuePayment[] = [];
  let summary: RevenueSummaryResponse | undefined;

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await fetchRevenuePage(getToken, filters, page, signal);
    const rows = (response?.results ?? []).map(toRevenuePayment);
    payments.push(...rows);

    if (page === 1) summary = response?.summary;

    // An empty page as well as `next: null` ends the walk — a page that says
    // there is another but returns nothing would otherwise spin to the cap.
    if (!response?.next || rows.length === 0) break;
  }

  payments.sort(
    (a, b) =>
      new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
  );

  return {
    payments,
    totals: summary ? toTotals(summary) : totalsFrom(payments),
  };
}
